import { useState, useCallback, useMemo, useEffect } from 'react';
import { masterSupabase as supabase } from "../../../lib/supabase-master.js";

export const useInvestmentCalculation = () => {
  // =====================================================
  // STATE MANAGEMENT
  // =====================================================
  
  const [calculationData, setCalculationData] = useState({
    // Objekt-Grunddaten (BESTEHEND - UNVERÄNDERT)
    purchase_price: 0,
    purchase_costs_percent: 10,
    living_space_sqm: 0,
    construction_year: new Date().getFullYear(),
    
    // Finanzierung (BESTEHEND - UNVERÄNDERT)
    equity: 0,
    loan_amount: 0,
    interest_rate: 3.5,
    repayment_rate: 2,
    
    // Mieteinnahmen (BESTEHEND - UNVERÄNDERT)
    monthly_rent: 0,
    utilities_monthly: 0,
    
    // Bewirtschaftungskosten (BESTEHEND - UNVERÄNDERT)
    management_costs_monthly: 0,
    maintenance_reserve_monthly: 0,
    insurance_costs_yearly: 0,
    property_tax_yearly: 0,
    other_operating_costs_monthly: 0,
    
    // Steuerliche Parameter (BESTEHEND - UNVERÄNDERT)
    depreciation_rate_percent: 2,
    tax_rate_percent: 42,
    special_depreciation_amount: 0,
    
    // =====================================================
    // NEUE FELDER - ERWEITERTE FINANZIERUNG
    // =====================================================
    loan_term_years: 30,
    interest_binding_years: 10,
    special_repayment_percent: 5.0,
    arrangement_fee_percent: 0,
    disagio_percent: 0,
    loan_insurance_monthly: 0,
    provision_interest_percent: 0.25,
    provision_interest_months: 6,
    
    // NEUE FELDER - ERWEITERTE MIETPARAMETER
    vacancy_rate: 2.0,
    annual_rent_increase: 2.0,
    
    // NEUE FELDER - ERWEITERTE STEUERPARAMETER
    building_value_percent: 80.0,
    depreciation_period_years: 50,
    
    // NEUE FELDER - ERWEITERTE NEBENKOSTEN
    broker_commission_percent: 3.57,
    property_transfer_tax_percent: 6.0,
    land_registry_fees: 0,
    notary_costs: 0,
    renovation_costs: 0
  });

  const [calculationName, setCalculationName] = useState('Neue Berechnung');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [currentCalculationId, setCurrentCalculationId] = useState(null);

  // =====================================================
  // BERECHNUNGSFUNKTIONEN (ERWEITERT)
  // =====================================================
  
  const calculations = useMemo(() => {
    const {
      purchase_price,
      purchase_costs_percent,
      equity,
      monthly_rent,
      management_costs_monthly,
      maintenance_reserve_monthly,
      insurance_costs_yearly,
      property_tax_yearly,
      other_operating_costs_monthly,
      interest_rate,
      loan_amount,
      depreciation_rate_percent,
      tax_rate_percent,
      // Neue Felder für erweiterte Berechnungen
      loan_term_years,
      special_repayment_percent,
      vacancy_rate,
      annual_rent_increase,
      building_value_percent,
      depreciation_period_years,
      arrangement_fee_percent,
      disagio_percent,
      broker_commission_percent,
      property_transfer_tax_percent,
      land_registry_fees,
      notary_costs,
      renovation_costs
    } = calculationData;

    // BESTEHENDE BERECHNUNGEN (UNVERÄNDERT)
    const purchase_costs = purchase_price * (purchase_costs_percent / 100);
    const total_investment = purchase_price + purchase_costs;
    const yearly_rent = monthly_rent * 12;
    
    // Bewirtschaftungskosten
    const monthly_operating_costs = 
      management_costs_monthly + 
      maintenance_reserve_monthly + 
      other_operating_costs_monthly +
      (insurance_costs_yearly / 12) +
      (property_tax_yearly / 12);
    
    const yearly_operating_costs = monthly_operating_costs * 12;
    
    // Finanzierungskosten
    const yearly_interest = loan_amount * (interest_rate / 100);
    
    // KPI-Berechnungen mit Division-by-Zero-Schutz
    const gross_rental_yield = purchase_price > 0 ? (yearly_rent / purchase_price) * 100 : 0;
    const net_rental_yield = purchase_price > 0 ? ((yearly_rent - yearly_operating_costs) / purchase_price) * 100 : 0;
    
    // Cashflow-Berechnung
    const monthly_cashflow = monthly_rent - monthly_operating_costs - (yearly_interest / 12);
    
    // Eigenkapitalrendite
    const equity_return = equity > 0 ? (monthly_cashflow * 12 / equity) * 100 : 0;
    
    // Steuerersparnis durch AfA
    const yearly_depreciation = purchase_price * (depreciation_rate_percent / 100);
    const tax_savings_yearly = yearly_depreciation * (tax_rate_percent / 100);
    const tax_savings_monthly = tax_savings_yearly / 12;

    // =====================================================
    // NEUE ERWEITERTE BERECHNUNGEN
    // =====================================================
    
    // Erweiterte Nebenkosten-Berechnung
    const detailed_purchase_costs = 
      (purchase_price * broker_commission_percent / 100) +
      (purchase_price * property_transfer_tax_percent / 100) +
      land_registry_fees +
      notary_costs;
    
    // KORRIGIERTE GESAMTKOSTEN-BERECHNUNG
    const total_investment_detailed = purchase_price + 
                                     (purchase_price * purchase_costs_percent / 100) + 
                                     renovation_costs + 
                                     notary_costs;
    
    // Effektive Mieteinnahmen mit Leerstand
    const effective_yearly_rent = yearly_rent * (1 - vacancy_rate / 100);
    const effective_monthly_rent = effective_yearly_rent / 12;
    
    // Erweiterte Finanzierungskosten
    const arrangement_fee = loan_amount * (arrangement_fee_percent / 100);
    const disagio_amount = loan_amount * (disagio_percent / 100);
    const total_loan_costs = arrangement_fee - disagio_amount; // Disagio reduziert Kosten
    
    // Monatliche Annuitäten-Rate (vereinfacht)
    const monthly_interest_rate = interest_rate / 100 / 12;
    const number_of_payments = loan_term_years * 12;
    const monthly_payment = loan_amount > 0 && monthly_interest_rate > 0 ? 
      loan_amount * (monthly_interest_rate * Math.pow(1 + monthly_interest_rate, number_of_payments)) / 
      (Math.pow(1 + monthly_interest_rate, number_of_payments) - 1) : 0;
    
    // Sondertilgungs-Effekt (vereinfacht)
    const annual_special_repayment = loan_amount * (special_repayment_percent / 100);
    const monthly_special_repayment = annual_special_repayment / 12;
    
    // Erweiterte Cashflow-Berechnung
    const extended_monthly_cashflow = effective_monthly_rent - monthly_operating_costs - monthly_payment;
    
    // Loan-to-Value Ratio
    const loan_to_value_ratio = purchase_price > 0 ? (loan_amount / purchase_price) * 100 : 0;
    
    // Debt Service Coverage Ratio
    const annual_debt_service = monthly_payment * 12;
    const net_operating_income = effective_yearly_rent - yearly_operating_costs;
    const debt_service_coverage_ratio = annual_debt_service > 0 ? net_operating_income / annual_debt_service : 0;
    
    // Cap Rate
    const cap_rate = purchase_price > 0 ? (net_operating_income / purchase_price) * 100 : 0;
    
    // Erweiterte AfA-Berechnung
    const building_value = purchase_price * (building_value_percent / 100);
    const extended_yearly_depreciation = building_value / depreciation_period_years;
    const extended_tax_savings_yearly = extended_yearly_depreciation * (tax_rate_percent / 100);
    
    // Cashflow nach Steuern
    const taxable_income = net_operating_income - yearly_interest - extended_yearly_depreciation;
    const tax_liability = Math.max(0, taxable_income * (tax_rate_percent / 100));
    const after_tax_cashflow = (extended_monthly_cashflow * 12) - tax_liability;

    return {
      // BESTEHENDE WERTE (UNVERÄNDERT)
      total_investment,
      purchase_costs,
      yearly_rent,
      monthly_operating_costs,
      yearly_operating_costs,
      gross_rental_yield,
      net_rental_yield,
      monthly_cashflow,
      equity_return,
      yearly_interest,
      tax_savings_monthly,
      tax_savings_yearly,
      
      // NEUE ERWEITERTE WERTE
      detailed_purchase_costs,
      total_investment_detailed,
      effective_yearly_rent,
      effective_monthly_rent,
      arrangement_fee,
      disagio_amount,
      total_loan_costs,
      monthly_payment,
      annual_special_repayment,
      monthly_special_repayment,
      extended_monthly_cashflow,
      loan_to_value_ratio,
      debt_service_coverage_ratio,
      cap_rate,
      building_value,
      extended_yearly_depreciation,
      extended_tax_savings_yearly,
      net_operating_income,
      taxable_income,
      tax_liability,
      after_tax_cashflow
    };
  }, [calculationData]);

  // =====================================================
  // CHART-DATEN GENERIERUNG (MOVED BEFORE KPIs)
  // =====================================================
  
  const generateCashflowProjection = useCallback(() => {
    const projection = [];
    let currentCashflow = (calculations.after_tax_cashflow || 0) / 12;
    
    for (let year = 1; year <= 10; year++) {
      // Annahme: 2% jährliche Mietsteigerung, 1% Kostensteigerung
      currentCashflow = currentCashflow * 1.01; // Vereinfachte Projektion
      projection.push({
        year,
        cashflow: Math.round(currentCashflow)
      });
    }
    
    return projection;
  }, [calculations]);

  const generateCostBreakdown = useCallback(() => {
    return [
      {
        category: 'Darlehen',
        amount: calculations.monthly_payment || 0
      },
      {
        category: 'Verwaltung',
        amount: calculationData.management_costs_monthly || 0
      },
      {
        category: 'Instandhaltung',
        amount: calculationData.maintenance_reserve_monthly || 0
      },
      {
        category: 'Versicherung',
        amount: (calculationData.insurance_costs_yearly || 0) / 12
      },
      {
        category: 'Steuern',
        amount: (calculationData.property_tax_yearly || 0) / 12
      },
      {
        category: 'Sonstige',
        amount: calculationData.other_operating_costs_monthly || 0
      }
    ].filter(item => item.amount > 0);
  }, [calculations, calculationData]);

  // =====================================================
  // KORRIGIERTE KPI-MAPPINGS FÜR DASHBOARD (NOW AFTER CHART FUNCTIONS)
  // =====================================================
  
  const kpis = useMemo(() => {
    return {
      // Rendite-KPIs
      grossRentalYield: calculations.gross_rental_yield || 0,
      netRentalYield: calculations.net_rental_yield || 0,
      equityReturn: calculations.equity_return || 0,
      equityPercent: calculationData.purchase_price > 0 ? (calculationData.equity / calculationData.purchase_price) * 100 : 0,
      
      // Cashflow-KPIs
      monthlyCashflowAfterTax: (calculations.after_tax_cashflow || 0) / 12,
      annualCashflowAfterTax: calculations.after_tax_cashflow || 0,
      isPositiveCashflow: ((calculations.after_tax_cashflow || 0) / 12) > 0,
      
      // KORRIGIERTE KOSTEN-KPIs
      monthlyOperatingCosts: calculations.monthly_operating_costs || 0,
      totalMonthlyLoanPayment: calculations.monthly_payment || 0, // KORRIGIERT: Verwende monthly_payment statt yearly_interest/12
      
      // Investment-KPIs
      totalInvestment: calculations.total_investment_detailed || calculations.total_investment || 0,
      purchaseCostsAbsolute: calculations.purchase_costs || 0,
      annualRentalIncome: calculations.yearly_rent || 0,
      
      // Weitere KPIs
      monthlyTotalIncome: calculationData.monthly_rent || 0,
      monthlyTotalCosts: calculations.monthly_operating_costs || 0,
      taxEffect: calculations.extended_tax_savings_yearly || calculations.tax_savings_yearly || 0,
      purchasePriceMultiplier: calculationData.purchase_price > 0 && calculationData.monthly_rent > 0 
        ? calculationData.purchase_price / (calculationData.monthly_rent * 12) 
        : 0,
      
      // Chart-Daten (NOW SAFE TO CALL)
      cashflowProjection: generateCashflowProjection(),
      costBreakdown: generateCostBreakdown()
    };
  }, [calculations, calculationData, generateCashflowProjection, generateCostBreakdown]);

  // =====================================================
  // INPUT HANDLERS (UNVERÄNDERT)
  // =====================================================
  
  const updateField = useCallback((field, value) => {
    setCalculationData(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  }, []);

  const updateCalculation = useCallback((updates) => {
    setCalculationData(prev => ({
      ...prev,
      ...updates
    }));
  }, []);

  // =====================================================
  // ERWEITERTE VALIDATION
  // =====================================================
  
  const validateData = useCallback(() => {
    const newErrors = {};
    
    // BESTEHENDE VALIDIERUNGEN (UNVERÄNDERT)
    if (calculationData.purchase_price <= 0) {
      newErrors.purchase_price = "Kaufpreis muss größer als 0 sein";
    }
    
    if (calculationData.interest_rate < 0 || calculationData.interest_rate > 20) {
      newErrors.interest_rate = "Zinssatz sollte zwischen 0% und 20% liegen";
    }
    
    if (calculationData.tax_rate_percent < 0 || calculationData.tax_rate_percent > 100) {
      newErrors.tax_rate_percent = "Steuersatz sollte zwischen 0% und 100% liegen";
    }
    
    // NEUE VALIDIERUNGEN
    if (calculationData.loan_term_years < 1 || calculationData.loan_term_years > 50) {
      newErrors.loan_term_years = "Darlehenslaufzeit sollte zwischen 1 und 50 Jahren liegen";
    }
    
    if (calculationData.interest_binding_years < 1 || calculationData.interest_binding_years > calculationData.loan_term_years) {
      newErrors.interest_binding_years = "Zinsbindung sollte zwischen 1 Jahr und der Darlehenslaufzeit liegen";
    }
    
    if (calculationData.special_repayment_percent < 0 || calculationData.special_repayment_percent > 10) {
      newErrors.special_repayment_percent = "Sondertilgung sollte zwischen 0% und 10% liegen";
    }
    
    if (calculationData.vacancy_rate < 0 || calculationData.vacancy_rate > 50) {
      newErrors.vacancy_rate = "Leerstandsrate sollte zwischen 0% und 50% liegen";
    }
    
    if (calculationData.building_value_percent < 50 || calculationData.building_value_percent > 95) {
      newErrors.building_value_percent = "Gebäudewertanteil sollte zwischen 50% und 95% liegen";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [calculationData]);

  // =====================================================
  // ERWEITERTE PLAUSIBILITÄTSPRÜFUNG
  // =====================================================
  
  const getInsights = useCallback(() => {
    const insights = [];
    
    // BESTEHENDE INSIGHTS (UNVERÄNDERT)
    if (calculations.gross_rental_yield < 2) {
      insights.push({
        type: 'warning',
        message: 'Bruttomietrendite unter 2% - sehr niedrige Rendite'
      });
    } else if (calculations.gross_rental_yield > 8) {
      insights.push({
        type: 'info',
        message: 'Sehr hohe Rendite - Nachhaltigkeit prüfen'
      });
    }
    
    if (calculations.monthly_cashflow < 0) {
      insights.push({
        type: 'error',
        message: 'Negativer Cashflow - Finanzierung überprüfen'
      });
    }
    
    if (calculationData.equity < calculationData.purchase_price * 0.2) {
      insights.push({
        type: 'info',
        message: 'Eigenkapital unter 20% - höhere Finanzierungskosten möglich'
      });
    }
    
    // NEUE ERWEITERTE INSIGHTS
    if (calculations.loan_to_value_ratio > 80) {
      insights.push({
        type: 'warning',
        message: 'Beleihungsgrad über 80% - höhere Zinsen möglich'
      });
    }
    
    if (calculations.debt_service_coverage_ratio < 1.2) {
      insights.push({
        type: 'error',
        message: 'Debt Service Coverage Ratio unter 1,2 - kritische Finanzierung'
      });
    }
    
    if (calculations.cap_rate < 3) {
      insights.push({
        type: 'warning',
        message: 'Cap Rate unter 3% - niedrige Objektrendite'
      });
    }
    
    if (calculationData.vacancy_rate > 5) {
      insights.push({
        type: 'info',
        message: 'Hohe Leerstandsrate - Standort und Objekt prüfen'
      });
    }
    
    if (calculations.after_tax_cashflow < 0) {
      insights.push({
        type: 'warning',
        message: 'Negativer Cashflow nach Steuern - Steueroptimierung prüfen'
      });
    }
    
    return insights;
  }, [calculations, calculationData]);

  // =====================================================
  // ERWEITERTE SAVE/LOAD FUNCTIONS
  // =====================================================
  
  // Alle gespeicherten Berechnungen laden
  const fetchSavedCalculations = useCallback(async () => {
    setIsLoading(true);
    
    try {
      const { data, error } = await supabase
        .from('investment_calculations')
        .select('id, calculation_name, created_at, purchase_price, monthly_rent, net_rental_yield, monthly_cashflow')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      setSavedCalculations(data || []);
      setIsLoading(false);
      return { success: true, data };
    } catch (error) {
      console.error('Fehler beim Laden der gespeicherten Berechnungen:', error);
      setIsLoading(false);
      return { success: false, error: error.message };
    }
  }, []);

  // Analyse löschen
  const deleteCalculation = useCallback(async (calculationId) => {
    setIsLoading(true);
    
    try {
      const { error } = await supabase
        .from('investment_calculations')
        .delete()
        .eq('id', calculationId);
      
      if (error) throw error;
      
      // Wenn die gelöschte Analyse aktuell geladen war, zurücksetzen
      if (currentCalculationId === calculationId) {
        setCurrentCalculationId(null);
      }
      
      // Liste aktualisieren
      await fetchSavedCalculations();
      setIsLoading(false);
      return { success: true };
      
    } catch (error) {
      console.error('Error deleting calculation:', error);
      setIsLoading(false);
      return { success: false, error: error.message };
    }
  }, [currentCalculationId, fetchSavedCalculations]);

  // Analyse-Name aktualisieren
  const updateCalculationName = useCallback(async (calculationId, newName) => {
    setIsLoading(true);
    
    try {
      const { error } = await supabase
        .from('investment_calculations')
        .update({ calculation_name: newName })
        .eq('id', calculationId);
      
      if (error) throw error;
      
      // Liste aktualisieren
      await fetchSavedCalculations();
      setIsLoading(false);
      return { success: true };
      
    } catch (error) {
      console.error('Error updating calculation name:', error);
      setIsLoading(false);
      return { success: false, error: error.message };
    }
  }, [fetchSavedCalculations]);

  const saveCalculation = useCallback(async (name = calculationName) => {
    if (!validateData()) {
      return { success: false, error: 'Validierungsfehler' };
    }
    
    setIsLoading(true);
    
    try {
      // Erweiterte Daten für Supabase vorbereiten
      const calculationToSave = {
        calculation_name: name,
        // KORREKTE FELDMAPPINGS FÜR DATENBANK
        purchase_price: calculationData.purchase_price || 0,
        purchase_costs_percent: calculationData.purchase_costs_percent || 10,
        living_space_sqm: calculationData.living_space_sqm || 0,
        construction_year: calculationData.construction_year || new Date().getFullYear(),
        equity: calculationData.equity || 0,
        loan1_amount: calculationData.loan_amount || 0,
        loan1_interest_rate: calculationData.interest_rate || 3.5,
        loan1_repayment_rate: calculationData.repayment_rate || 2,
        monthly_rent: calculationData.monthly_rent || 0,
        monthly_ancillary_costs: calculationData.utilities_monthly || 0,
        management_costs_monthly: calculationData.management_costs_monthly || 0,
        maintenance_reserve_monthly: calculationData.maintenance_reserve_monthly || 0,
        insurance_costs_yearly: calculationData.insurance_costs_yearly || 0,
        property_tax_yearly: calculationData.property_tax_yearly || 0,
        other_operating_costs_monthly: calculationData.other_operating_costs_monthly || 0,
        tax_rate_percent: calculationData.tax_rate_percent || 42,
        building_value_percent: calculationData.building_value_percent || 80,
        depreciation_period_years: calculationData.depreciation_period_years || 50,
        // BERECHNETE WERTE
        gross_rental_yield: calculations.gross_rental_yield,
        net_rental_yield: calculations.net_rental_yield,
        equity_return: calculations.equity_return,
        monthly_cashflow: calculations.monthly_cashflow,
        yearly_interest: calculations.yearly_interest,
        total_investment: calculations.total_investment_detailed || calculations.total_investment,
        user_id: (await supabase.auth.getUser()).data.user?.id
      };
      
      const { data, error } = await supabase
        .from('investment_calculations')
        .insert(calculationToSave)
        .select()
        .single();
      
      if (error) throw error;
      
      setCurrentCalculationId(data.id);
      await fetchSavedCalculations();
      setIsLoading(false);
      return { success: true, data };
    } catch (error) {
      console.error('Fehler beim Speichern:', error);
      setIsLoading(false);
      return { success: false, error: error.message };
    }
  }, [calculationData, calculations, validateData, calculationName, fetchSavedCalculations]);

  const loadCalculation = useCallback(async (id) => {
    setIsLoading(true);
    
    try {
      const { data, error } = await supabase
        .from('investment_calculations')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      
      // KORREKTE FELDMAPPINGS FÜR LADEN (von DB zu Frontend)
      const inputFields = {
        purchase_price: data.purchase_price || 0,
        purchase_costs_percent: data.purchase_costs_percent || 10,
        living_space_sqm: data.living_space_sqm || 0,
        construction_year: data.construction_year || new Date().getFullYear(),
        equity: data.equity || 0,
        loan_amount: data.loan1_amount || 0,
        interest_rate: data.loan1_interest_rate || 3.5,
        repayment_rate: data.loan1_repayment_rate || 2,
        monthly_rent: data.monthly_rent || 0,
        utilities_monthly: data.monthly_ancillary_costs || 0,
        management_costs_monthly: data.management_costs_monthly || 0,
        maintenance_reserve_monthly: data.maintenance_reserve_monthly || 0,
        insurance_costs_yearly: data.insurance_costs_yearly || 0,
        property_tax_yearly: data.property_tax_yearly || 0,
        other_operating_costs_monthly: data.other_operating_costs_monthly || 0,
        tax_rate_percent: data.tax_rate_percent || 42,
        building_value_percent: data.building_value_percent || 80,
        depreciation_period_years: data.depreciation_period_years || 50,
        special_depreciation_amount: data.special_depreciation_amount || 0,
        // Erweiterte Felder
        loan_term_years: data.loan_term_years || 30,
        interest_binding_years: data.interest_binding_years || 10,
        special_repayment_percent: data.special_repayment_percent || 5.0,
        arrangement_fee_percent: data.arrangement_fee_percent || 0,
        disagio_percent: data.disagio_percent || 0,
        loan_insurance_monthly: data.loan_insurance_monthly || 0,
        provision_interest_percent: data.provision_interest_percent || 0.25,
        provision_interest_months: data.provision_interest_months || 6,
        vacancy_rate: data.vacancy_rate || 2.0,
        annual_rent_increase: data.annual_rent_increase || 2.0,
        broker_commission_percent: data.broker_commission_percent || 3.57,
        property_transfer_tax_percent: data.property_transfer_tax_percent || 6.0,
        land_registry_fees: data.land_registry_fees || 0,
        notary_costs: data.notary_costs || 0,
        renovation_costs: data.renovation_costs || 0
      };
      
      setCalculationData(inputFields);
      setCalculationName(data.calculation_name || 'Neue Berechnung');
      setCurrentCalculationId(id);
      setIsLoading(false);
      return { success: true, data };
    } catch (error) {
      console.error('Fehler beim Laden:', error);
      setIsLoading(false);
      return { success: false, error: error.message };
    }
  }, []);

  // =====================================================
  // NEUE ERWEITERTE FUNKTIONEN
  // =====================================================
  
  // Tilgungsplan generieren
  const generateAmortizationSchedule = useCallback(() => {
    const { loan_amount, interest_rate, loan_term_years, special_repayment_percent } = calculationData;
    
    if (loan_amount <= 0 || interest_rate <= 0) return [];
    
    const schedule = [];
    let remainingBalance = loan_amount;
    const monthlyInterestRate = interest_rate / 100 / 12;
    const numberOfPayments = loan_term_years * 12;
    const monthlyPayment = calculations.monthly_payment;
    const annualSpecialRepayment = loan_amount * (special_repayment_percent / 100);
    
    for (let month = 1; month <= numberOfPayments && remainingBalance > 0; month++) {
      const interestPayment = remainingBalance * monthlyInterestRate;
      const principalPayment = monthlyPayment - interestPayment;
      const specialPayment = month % 12 === 0 ? annualSpecialRepayment : 0;
      
      const totalPrincipal = Math.min(principalPayment + specialPayment, remainingBalance);
      remainingBalance -= totalPrincipal;
      
      schedule.push({
        month,
        year: Math.ceil(month / 12),
        interestPayment: Math.round(interestPayment * 100) / 100,
        principalPayment: Math.round(totalPrincipal * 100) / 100,
        remainingBalance: Math.round(remainingBalance * 100) / 100
      });
    }
    
    return schedule;
  }, [calculationData, calculations]);

  // Auto-load beim Start
  useEffect(() => {
    fetchSavedCalculations();
  }, [fetchSavedCalculations]);

  return {
    // State
    calculationData,
    calculationName,
    setCalculationName,
    isLoading,
    errors,
    savedCalculations,
    currentCalculationId,
    
    // Berechnungen
    calculations,
    kpis,
    
    // Funktionen
    updateField,
    updateCalculation,
    validateData,
    getInsights,
    saveCalculation,
    loadCalculation,
    fetchSavedCalculations,
    deleteCalculation,
    updateCalculationName,
    
    // Neue erweiterte Funktionen
    generateAmortizationSchedule
  };
};

